import React, { useEffect, useState } from 'react'
import { Routes, Route, Link, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { OrdersPage } from './pages/OrdersPage'
import { OrderDetailPage } from './pages/OrderDetailPage'
import { LoginPage } from './pages/LoginPage'
import { ProfilePage } from './pages/ProfilePage'
import { DocumentsPage } from './pages/DocumentsPage'
import { ErrorBoundary } from './providers/ErrorBoundary'
import { useGlobalWebSocket } from '../shared/ws/useGlobalWebSocket'
import { useAuth } from '../shared/auth/store'
import { useTelegramWebApp } from '../shared/auth/TelegramProvider'
import { initAuth } from '../shared/auth/initAuth'
import { ProfileDropdown } from '../shared/components/ProfileDropdown'
import { SyncIndicator } from '../shared/components/SyncIndicator'
import { NotificationBell } from '../shared/components/NotificationBell'
import { RegistrationForm } from '../shared/components/auth/RegistrationForm'
import { AccessDeniedScreen } from '../shared/components/AccessDeniedScreen'

import { useRealtimeSystem } from '../shared/store/realtimeSync'
import { useZoomControl } from '../shared/hooks/useZoomControl'
import { useNotificationsInit } from '../shared/hooks/useNotifications'
import { ToastProvider } from '../shared/contexts/ToastContext'
import { logger } from '../shared/utils/logger'
import { ChatWidget } from '../modules/chat'
import { MiniAppHandler } from './components/MiniAppHandler'



// Компонент анимированного фона
const AnimatedBackground: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    color: 'white',
    fontFamily: 'system-ui, -apple-system, sans-serif'
  }}>
    {/* Анимированные частицы на фоне */}
    <div style={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      overflow: 'hidden',
      pointerEvents: 'none'
    }}>
      {[...Array(20)].map((_, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            width: Math.random() * 4 + 2 + 'px',
            height: Math.random() * 4 + 2 + 'px',
            background: 'rgba(255, 255, 255, 0.3)',
            borderRadius: '50%',
            left: Math.random() * 100 + '%',
            top: Math.random() * 100 + '%',
            animation: `float ${Math.random() * 3 + 2}s ease-in-out infinite alternate`
          }}
        />
      ))}
    </div>

    {/* Контент */}
    <div style={{ zIndex: 1, width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {children}
    </div>

    {/* CSS анимации */}
    <style>{`
      @keyframes pulse {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.1); }
      }
      
      @keyframes bounce {
        0%, 80%, 100% { 
          transform: scale(0);
        } 40% { 
          transform: scale(1);
        }
      }
      
      @keyframes float {
        0% { transform: translateY(0px); }
        100% { transform: translateY(-20px); }
      }
    `}</style>
  </div>
)

// Компонент загрузки
const LoadingScreen: React.FC = () => {
  const [showWelcome, setShowWelcome] = React.useState(true)
  const [currentMessage, setCurrentMessage] = React.useState(0)
  
  const welcomeMessages = [
    "Добро пожаловать!",
    "Подготавливаем всё для вас...",
    "Загружаем данные...",
    "Почти готово!"
  ]

  React.useEffect(() => {
    const messageInterval = setInterval(() => {
      setCurrentMessage(prev => (prev + 1) % welcomeMessages.length)
    }, 1500) // Ускоряем смену сообщений

    // Минимальная задержка 2 секунды для плавности UX
    const minimumDelayTimer = setTimeout(() => {
      setShowWelcome(false)
    }, 2000)

    return () => {
      clearInterval(messageInterval)
      clearTimeout(minimumDelayTimer)
    }
  }, [])

  return (
    <AnimatedBackground>
      <div style={{
        textAlign: 'center',
        maxWidth: '320px',
        padding: '2rem'
      }}>
        {/* Логотип */}
        <div style={{
          marginBottom: '1.5rem',
          animation: 'pulse 2s ease-in-out infinite',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <img 
            src="/assets/images/advance-logo.png" 
            alt="Advance Logo" 
            style={{
              width: '90px', // Уменьшено на 25% (120px * 0.75 = 90px)
              height: 'auto',
              maxWidth: '100%',
              filter: 'brightness(0) invert(1)', // Делаем логотип белым
              opacity: 0.9
            }}
          />
        </div>

        {/* Приветственное сообщение */}
        <h1 style={{
          fontSize: '1.8rem',
          fontWeight: '600',
          marginBottom: '1rem',
          textShadow: '0 2px 4px rgba(0,0,0,0.3)',
          opacity: showWelcome ? 1 : 0,
          transform: showWelcome ? 'translateY(0)' : 'translateY(-10px)',
          transition: 'all 0.5s ease'
        }}>
          {welcomeMessages[currentMessage]}
        </h1>

        {/* Анимированный индикатор загрузки */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '0.5rem',
          marginBottom: '1rem'
        }}>
          <div style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            background: 'white',
            animation: 'bounce 1.4s ease-in-out infinite both',
            animationDelay: '0s'
          }} />
          <div style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            background: 'white',
            animation: 'bounce 1.4s ease-in-out infinite both',
            animationDelay: '0.16s'
          }} />
          <div style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            background: 'white',
            animation: 'bounce 1.4s ease-in-out infinite both',
            animationDelay: '0.32s'
          }} />
        </div>

        {/* Подпись */}
        <p style={{
          fontSize: '0.9rem',
          opacity: 0.8,
          fontWeight: '300'
        }}>
          Система управления заказами
        </p>
      </div>
    </AnimatedBackground>
  )
}

// Компонент для неавторизованных пользователей
const UnauthenticatedScreen: React.FC = () => {
  const { error, needsRegistration, pendingApproval, mode } = useAuth()
  
  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '100vh',
      flexDirection: 'column',
      gap: 16,
      padding: 20,
      textAlign: 'center'
    }}>
      <h1>🔐 Требуется авторизация</h1>
      
      {needsRegistration && (
        <div style={{ color: 'orange' }}>
          📝 Требуется регистрация. Обратитесь к администратору.
        </div>
      )}
      
      {pendingApproval && (
        <div style={{ color: 'blue' }}>
          ⏰ Ваша заявка на рассмотрении у администратора.
        </div>
      )}
      
      {error && (
        <div style={{ color: 'red', fontSize: 14 }}>
          ❌ Ошибка: {error}
        </div>
      )}
      
      <div style={{ fontSize: 12, color: '#666' }}>
        Режим: {mode === 'telegram' ? '📱 Telegram' : mode === 'browser' ? '🌐 Браузер' : '❓ Неизвестно'}
      </div>
      
      <button 
        onClick={() => initAuth()}
        style={{
          padding: '12px 24px',
          fontSize: 16,
          backgroundColor: '#0088cc',
          color: 'white',
          border: 'none',
          borderRadius: 8,
          cursor: 'pointer'
        }}
      >
        🔄 Повторить авторизацию
      </button>
    </div>
  )
}

export const App: React.FC = () => {
  const { isConnected } = useGlobalWebSocket()
  const { isLoading, isAuthenticated, user, mode, isTelegramMode, isBrowserMode, needsRegistration, pendingApproval, error, isCheckingAccess } = useAuth()
  const { themeParams, colorScheme } = useTelegramWebApp()
  const navigate = useNavigate()
  const [isRetryLoading, setIsRetryLoading] = useState(false)
  const currentLocation = useLocation()
  
  // Управление зумом в зависимости от режима приложения
  const { isZoomDisabled } = useZoomControl()
  
  // Инициализация системы уведомлений
  useNotificationsInit()
  
  // Инициализация реал-тайм системы
  useRealtimeSystem()

  // 🔥 СЧЕТЧИКИ ЧАТОВ ТЕПЕРЬ ЗАГРУЖАЮТСЯ В ordersStore.loadAllOrders()
  // Никаких дополнительных инициализаций не нужно!
  

  
  // Состояние авторизации отслеживается через store

  // Проверяем URL и перенаправляем при необходимости
  useEffect(() => {
    // Если URL содержит порт :8081, перенаправляем на правильный домен
    if (window.location.hostname === 'cabinet.advatool.ru' && window.location.port === '8081') {
      const newUrl = `https://cabinet.advatool.ru/${window.location.pathname}${window.location.search}${window.location.hash}`
      window.location.replace(newUrl)
      return
    }
  }, [])

  // Применяем тему Telegram для стилизации
  useEffect(() => {
    if (isTelegramMode && themeParams) {
      const root = document.documentElement
      Object.entries(themeParams).forEach(([key, value]) => {
        if (typeof value === 'string') {
          root.style.setProperty(`--tg-${key}`, value)
        }
      })
      root.setAttribute('data-telegram-theme', colorScheme)
    }
  }, [isTelegramMode, themeParams, colorScheme])

  // Отладочная информация для диагностики белого экрана
  if (import.meta.env.DEV) {
    console.log('🔥 ОТЛАДКА App.tsx: Состояние рендеринга', {
      isLoading,
      isRetryLoading,
      isAuthenticated,
      isCheckingAccess,
      mode,
      user: user ? 'есть' : 'нет',
      needsRegistration,
      pendingApproval,
      error
    })
    
    // КРИТИЧНО: Отправляем диагностику на backend для логирования
    logger.log('🔥 ДИАГНОСТИКА App.tsx: Состояние рендеринга', {
      isLoading,
      isRetryLoading,
      isAuthenticated,
      isCheckingAccess,
      mode,
      user: user ? 'есть' : 'нет',
      needsRegistration,
      pendingApproval,
      error,
      url: window.location.href,
      pathname: window.location.pathname,
      search: window.location.search,
      hash: window.location.hash
    })
  }

  // Состояние загрузки или проверки доступа
  if (isLoading || isRetryLoading || (isAuthenticated && isCheckingAccess)) {
    // Показываем экран загрузки
    return <LoadingScreen />
  }

  // Состояние неавторизованного пользователя
  if (!isAuthenticated) {
    
    // TELEGRAM РЕЖИМ: Обрабатываем специальные состояния
    if (mode === 'telegram') {
      // ВАЖНО: Если мы на странице /miniapp/login или /miniapp/reset-password, всегда показываем LoginPage
      if (currentLocation.pathname === '/miniapp/login' || currentLocation.pathname.startsWith('/miniapp/reset-password')) {
        return (
          <ErrorBoundary>
            <Routes>
              <Route path="/miniapp/login" element={<LoginPage />} />
              <Route path="/miniapp/reset-password" element={<LoginPage />} />
              <Route path="*" element={<Navigate to="/miniapp/login" replace />} />
            </Routes>
          </ErrorBoundary>
        )
      }
      
      // Если контакт не найден по telegram_id - показываем форму регистрации/входа
      if (needsRegistration) {
        return (
          <AnimatedBackground>
            <div style={{ padding: '1rem', width: '100%', display: 'flex', justifyContent: 'center' }}>
              <RegistrationForm
                onSuccess={async (token, user) => {
                  // Обновляем состояние авторизации
                  const { useAuthStore } = await import('../shared/auth/store')
                  useAuthStore.getState().setAuth(token, user, 'telegram')
                }}
                onError={(message) => {
                  logger.error('Ошибка регистрации:', message)
                  alert(`Ошибка регистрации: ${message}`)
                }}
                onSwitchToLogin={() => {
                  // Перенаправляем на LoginPage в MiniApp, чтобы сохранить режим и telegram_id
                  navigate('/miniapp/login')
                }}
                onSwitchToPasswordReset={() => {
                  // Перенаправляем на LoginPage в режиме сброса пароля
                  navigate('/miniapp/login?mode=password_reset')
                }}
              />
            </div>
          </AnimatedBackground>
        )
      }
      
      if (pendingApproval) {
        return (
          <AccessDeniedScreen 
            message="У вас нет доступа к нашей системе"
            onRetry={async () => {
              // Повторная проверка доступа через API вместо перенаправления на форму входа
              logger.log('🔄 Повторная проверка доступа...')
              setIsRetryLoading(true)
              try {
                const success = await initAuth()
                if (!success) {
                  logger.log('❌ Доступ по-прежнему отсутствует')
                }
              } catch (error) {
                logger.error('❌ Ошибка при повторной проверке доступа:', error)
              } finally {
                setIsRetryLoading(false)
              }
            }}
          />
        )
      }
      
      // Проверяем тип ошибки для показа соответствующего экрана
      const isAccessDenied = error === 'access_denied' ||
                             error?.includes('доступ') || error?.includes('Доступ') || 
                             error?.includes('access') || error?.includes('заблокирован') ||
                             error?.includes('менеджер') || error?.includes('компании')
      
      if (isAccessDenied) {
        return (
          <AccessDeniedScreen 
            message={error || "У вас нет доступа к системе. Обратитесь к менеджеру вашей компании для активации доступа."}
            onRetry={async () => {
              // Повторная проверка доступа через API вместо перенаправления на форму входа
              logger.log('🔄 Повторная проверка доступа...')
              setIsRetryLoading(true)
              try {
                const success = await initAuth()
                if (!success) {
                  logger.log('❌ Доступ по-прежнему отсутствует')
                }
              } catch (error) {
                logger.error('❌ Ошибка при повторной проверке доступа:', error)
              } finally {
                setIsRetryLoading(false)
              }
            }}
          />
        )
      }

      // Общая ошибка авторизации в Telegram
      return (
        <AnimatedBackground>
          <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center shadow-2xl">
            <div className="text-6xl mb-4">❌</div>
            <h1 className="text-2xl font-bold text-gray-800 mb-4">Ошибка авторизации</h1>
            <p className="text-gray-600 mb-6">
              {error || 'Не удалось авторизоваться в Telegram Mini App. Попробуйте перезапустить приложение.'}
            </p>
            <button 
              onClick={() => window.location.reload()}
              className="bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-lg font-medium transition-colors"
            >
              🔄 Перезапустить
            </button>
          </div>
        </AnimatedBackground>
      )
    }
    
    // БРАУЗЕРНЫЙ РЕЖИМ: Показываем форму входа
    if (mode === 'browser' || mode === 'unknown') {
      return (
        <ErrorBoundary>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<LoginPage />} />
            <Route path="/reset-password" element={<LoginPage />} />
            <Route path="*" element={
              <Navigate 
                to="/login" 
                replace
              />
            } />
          </Routes>
        </ErrorBoundary>
      )
    }
    
    // Fallback
    return <UnauthenticatedScreen />
  }

  // Главное приложение для авторизованных пользователей
  if (import.meta.env.DEV) {
    console.log('🔥 ОТЛАДКА: Рендерим основное приложение для авторизованного пользователя')
  }
  
  const location = useLocation()
  const isOrderDetailPage = location.pathname.startsWith('/orders/') && location.pathname !== '/orders'
  
  // Извлекаем orderId из URL для автоматического открытия чата заказа
  const getOrderIdFromPath = (pathname: string): string | undefined => {
    const orderDetailMatch = pathname.match(/\/(?:miniapp\/)?orders\/(\d+)/)
    return orderDetailMatch ? orderDetailMatch[1] : undefined
  }
  
  // Также проверяем URL параметры для MiniApp
  const searchParams = new URLSearchParams(location.search)
  const orderIdFromParams = searchParams.get('order_id')
  
  const currentOrderId = getOrderIdFromPath(location.pathname) || orderIdFromParams

  return (
    <ToastProvider>
      <ErrorBoundary>
      <div style={{
        minHeight: '100vh',
        backgroundColor: isTelegramMode ? 'var(--tg-bg-color, #ffffff)' : '#ffffff',
        color: isTelegramMode ? 'var(--tg-text-color, #000000)' : '#000000'
      }}>
        {/* Компактная навигация с логотипом и профилем (скрываем на странице детального просмотра) */}
        {!isOrderDetailPage && (
          <nav className="bg-white/90 backdrop-blur-sm border-b border-gray-200/50 shadow-sm relative z-40">
            <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6">
              <div className="flex items-center justify-between h-12 sm:h-14">
                {/* Логотип и название компании слева */}
                <Link 
                  to={isTelegramMode ? "/miniapp/orders" : "/orders"}
                  className="flex items-center space-x-2 hover:opacity-80 transition-opacity duration-200 cursor-pointer"
                >
                  <img 
                    src="/assets/images/advance-logo.png" 
                    alt="Advance Logo" 
                    className="h-8 w-8 sm:h-9 sm:w-9 md:h-10 md:w-10 object-contain"
                  />
                  <span className="text-xl sm:text-2xl md:text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent hidden xs:block">
                    Advance
                  </span>
                  {/* Мобильная версия названия */}
                  <span className="text-lg font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent xs:hidden">
                    Advance
                  </span>
                </Link>

                {/* Профиль справа */}
                <div className="flex items-center space-x-2">

                  {/* Колокольчик уведомлений */}
                  <NotificationBell />
                  
                  {/* Индикатор синхронизации - временно скрыт */}
                  {/* <div className="relative">
                    <SyncIndicator showDetails={true} />
                  </div> */}
                  
                  <ProfileDropdown 
                  onLogout={() => {
                    if (confirm('Вы уверены, что хотите выйти из системы?')) {
                      logger.log('🚪 Выполняется выход из системы...')
                      
                      // Импортируем и вызываем logout
                      import('../shared/auth/initAuth').then(({ logout }) => {
                        logout()
                        logger.log('🔄 Перенаправление на страницу входа...')
                        // Очищаем историю навигации и перенаправляем на login без state
                        window.history.replaceState(null, '', '/login')
                        window.location.href = '/login'
                      }).catch((error) => {
                        logger.error('Ошибка при выходе:', error)
                        // Фаллбек - просто очищаем localStorage
                        localStorage.clear()
                        window.history.replaceState(null, '', '/login')
                        window.location.href = '/login'
                      })
                    }
                  }}
                />
                </div>
              </div>
            </div>
          </nav>
        )}

        {/* Маршруты */}
        <Routes>
          {/* Telegram Mini App routes */}
          <Route path="/miniapp" element={<MiniAppHandler />} />
          <Route path="/miniapp/orders" element={<OrdersPage />} />
          <Route path="/miniapp/orders/:orderId" element={<OrderDetailPage />} />
          <Route path="/miniapp/profile" element={<ProfilePage />} />
          <Route path="/miniapp/documents" element={<DocumentsPage />} />
          <Route path="/miniapp/registration" element={<div>Telegram Registration Page</div>} />
          {/* MiniApp Login page to allow email/password linking with telegram_id */}
          <Route path="/miniapp/login" element={<LoginPage />} />
          <Route path="/miniapp/reset-password" element={<LoginPage />} />
          
          {/* Web App routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<div>Web Registration Page</div>} />
          <Route path="/reset-password" element={<LoginPage />} />
          <Route path="/orders" element={<OrdersPage />} />
          <Route path="/orders/:orderId" element={<OrderDetailPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/documents" element={<DocumentsPage />} />
          
          {/* Default redirects based on platform */}
          <Route path="*" element={
            <Navigate 
              to={isTelegramMode ? "/miniapp/orders" : "/orders"} 
              replace 
            />
          } />
        </Routes>

        {/* Глобальный виджет чата - показываем только для авторизованных пользователей */}
        <ChatWidget 
          orderId={currentOrderId || undefined} // Автоматически открывает чат заказа на странице деталей
          onUnreadCountChange={(count) => {
            // Можно интегрировать с системой уведомлений
            logger.debug(`💬 Непрочитанных сообщений в чате: ${count}`)
          }}
          onMessageSent={(message) => {
            logger.debug('💬 Сообщение отправлено:', message)
          }}
          onChatSelect={(chat) => {
            logger.debug('💬 Выбран чат:', chat)
          }}
        />
        


      </div>
      
    </ErrorBoundary>
    </ToastProvider>
  )
}

export default App
